import { Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Aviso } from '@/components/ui/aviso';
import { Cifra, Cifras } from '@/components/ui/cifras';
import { Etiqueta } from '@/components/ui/etiqueta';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatoCantidad, formatoPrecio, haceCuanto } from '@/lib/utils/formato-precio';
import { strategyApplicationsApi } from '../services/strategy-applications-api';
import type {
  AppliedStrategy,
  EstadoEnLaEstrategia,
  ProductosDeLaEstrategia,
} from '../types/strategy-applications.types';
import { avisoDeComoToma } from '../lib/como-toma-los-productos';
import { EncabezadoDeVentana, PIE_DE_VENTANA } from '@/features/compite/ui/ventana-compite';

/**
 * Todos los productos que alcanza una estrategia (lote B2, spec 246 punto 6;
 * maqueta #b2-productos): precio de hoy, lo que pide, dónde quedaría y cómo
 * está. Las cifras filtran con un toque; el buscador busca entre ellos. Se abre
 * desde «N productos» de la tarjeta.
 */

/** Un monto, o «—» con nombre para el lector de pantalla (auditoría H19): ausente es «no se sabe». */
function Monto({ n }: { n: number | null }) {
  return n == null ? (
    <span role="img" aria-label="No se sabe">
      —
    </span>
  ) : (
    <>{formatoPrecio(n)}</>
  );
}

export function ProductosDeLaEstrategiaDialog({
  estrategia,
  subtitulo,
  onCerrar,
  onIrAPorDecidir,
}: {
  estrategia: AppliedStrategy | null;
  /** «Apuntar a un margen · 27 % · siempre te pregunta · sólo cuando lo pedís». */
  subtitulo: string;
  onCerrar: () => void;
  /** «Espera tu decisión →»: Por decidir, con esta estrategia y la fila de ese producto abierta (C2b). */
  onIrAPorDecidir?: (strategyApplicationId: string, productId: string) => void;
}) {
  const [estado, setEstado] = useState<EstadoEnLaEstrategia | null>(null);
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState<ProductosDeLaEstrategia | null>(null);
  const [error, setError] = useState(false);
  const [vuelta, setVuelta] = useState(0);

  useEffect(() => {
    setEstado(null);
    setBuscar('');
    setPagina(1);
    setDatos(null);
  }, [estrategia?.id]);

  useEffect(() => {
    if (!estrategia) return;
    let vigente = true;
    setError(false);
    const t = setTimeout(
      () => {
        strategyApplicationsApi
          .productosDeLaEstrategia(estrategia.id, {
            q: buscar.trim() || undefined,
            estado: estado ?? undefined,
            page: pagina,
          })
          .then((d) => vigente && setDatos(d))
          .catch(() => vigente && setError(true));
      },
      buscar ? 300 : 0
    );
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [estrategia, estado, buscar, pagina, vuelta]);

  if (!estrategia) return null;
  const aviso = avisoDeComoToma(estrategia, datos?.entraronEstaSemana ?? 0);
  const total = datos?.cifras.alcanza ?? null;
  const filtrar = (e: EstadoEnLaEstrategia | null) => {
    setEstado((actual) => (actual === e ? null : e));
    setPagina(1);
  };
  const paginas = datos ? Math.max(1, Math.ceil(datos.total / 50)) : 1;

  return (
    <Dialog open onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col overflow-hidden">
        <EncabezadoDeVentana icono={Package}>
          <DialogTitle>
            {estrategia.name}
            {total != null &&
              ` · ${formatoCantidad(total)} ${total === 1 ? 'producto' : 'productos'}`}
          </DialogTitle>
          <DialogDescription>{subtitulo}</DialogDescription>
        </EncabezadoDeVentana>

        <div className="-mx-6 min-h-0 flex-1 space-y-3 overflow-y-auto px-6">
          <Aviso tono="info" titulo={aviso.titulo}>
            {aviso.texto}
          </Aviso>

          {error ? (
            <EstadoError onReintentar={() => setVuelta((v) => v + 1)}>
              No se pudieron leer los productos de esta estrategia.
            </EstadoError>
          ) : !datos ? (
            <div className="space-y-2" data-testid="cargando-productos">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : (
            <>
              <Cifras>
                <Cifra
                  valor={datos.cifras.alcanza}
                  rotulo="alcanza hoy"
                  activa={estado == null}
                  onClick={() => filtrar(null)}
                />
                <Cifra
                  valor={datos.cifras.esperan}
                  rotulo="esperan tu decisión"
                  tono={datos.cifras.esperan > 0 ? 'alerta' : 'neutro'}
                  activa={estado === 'ESPERA'}
                  onClick={() => filtrar('ESPERA')}
                />
                <Cifra
                  valor={datos.cifras.alDia}
                  rotulo="al día"
                  activa={estado === 'AL_DIA'}
                  onClick={() => filtrar('AL_DIA')}
                />
                <Cifra
                  valor={datos.cifras.sinCosto}
                  rotulo="sin costo: no se puede calcular el margen"
                  tono={datos.cifras.sinCosto > 0 ? 'alerta' : 'neutro'}
                  activa={estado === 'SIN_COSTO'}
                  onClick={() => filtrar('SIN_COSTO')}
                />
              </Cifras>
              <Input
                aria-label={`Buscar en estos ${formatoCantidad(datos.cifras.alcanza)}`}
                placeholder={`Buscar en estos ${formatoCantidad(datos.cifras.alcanza)}`}
                className="max-w-sm"
                value={buscar}
                onChange={(e) => {
                  setBuscar(e.target.value);
                  setPagina(1);
                }}
              />
              {datos.filas.length === 0 ? (
                <EstadoVacio titulo="Ninguno con ese filtro">
                  {datos.cifras.alcanza === 0
                    ? 'Hoy esta estrategia no alcanza a ningún producto.'
                    : 'Probá con otra cifra o con otro nombre.'}
                </EstadoVacio>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Producto</TableHead>
                      <TableHead>Categoría · marca</TableHead>
                      <TableHead className="text-right">Hoy (Bs)</TableHead>
                      <TableHead className="text-right">Pide (Bs)</TableHead>
                      <TableHead className="text-right">Quedaría (Bs)</TableHead>
                      <TableHead>Cómo está</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {datos.filas.map((f) => (
                      <TableRow key={f.productId} data-testid="producto-de-la-estrategia">
                        <TableCell>
                          <span className="block font-medium">{f.name}</span>
                          <span className="text-xs text-muted-foreground">{f.sku}</span>
                        </TableCell>
                        <TableCell>
                          {f.category ?? (
                            <span role="img" aria-label="Sin categoría">
                              —
                            </span>
                          )}
                          {f.brand && (
                            <span className="block text-xs text-muted-foreground">{f.brand}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <Monto n={f.precioHoy} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <Monto n={f.pide} />
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          <Monto n={f.quedaria} />
                        </TableCell>
                        <TableCell>
                          {f.estado === 'ESPERA' ? (
                            onIrAPorDecidir ? (
                              <button
                                type="button"
                                className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                onClick={() => onIrAPorDecidir(estrategia.id, f.productId)}
                              >
                                <Etiqueta tono="alerta">Espera tu decisión →</Etiqueta>
                              </button>
                            ) : (
                              <Etiqueta tono="alerta">Espera tu decisión</Etiqueta>
                            )
                          ) : f.estado === 'SIN_COSTO' ? (
                            <Etiqueta tono="alerta">Sin costo</Etiqueta>
                          ) : (
                            <Etiqueta tono="ok">Al día</Etiqueta>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="text-xs text-muted-foreground">
                Mostrando {formatoCantidad(datos.filas.length)} de {formatoCantidad(datos.total)}.
                «Pide» es lo que calcula la estrategia; «Quedaría», después de los límites y el
                redondeo.{' '}
                {datos.calculadaEn
                  ? `Calculado ${haceCuanto(datos.calculadaEn)}, con la foto del catálogo.`
                  : 'La foto del catálogo todavía no se calculó.'}
              </p>
              {paginas > 1 && (
                <div className="flex items-center gap-2 text-sm">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagina <= 1}
                    onClick={() => setPagina((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span className="text-muted-foreground">
                    Página {pagina} de {paginas}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={pagina >= paginas}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className={PIE_DE_VENTANA}>
          <Button variant="outline" onClick={onCerrar}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
